import React from 'react';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import { charityConfig } from '../../config/charity';
import InlineTranslationEdit from '../InlineTranslationEdit';
import { useContent } from '../../contexts/ContentContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';

export default function AceOfHeartsCharity({ editMode = false, handleTranslationUpdate }) {
  const { c } = useContent();
  const { siteDetails } = useSiteConfig() || {};
  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl;
  const brandName = siteDetails?.siteName || charityConfig.name;
  return (
    <div className="relative overflow-hidden bg-white dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 rounded-2xl shadow-lg dark:shadow-2xl border border-red-200 dark:border-red-500/30">
      {/* Background Ace decorations */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div className="absolute top-4 left-4 text-6xl text-red-500 font-serif">A</div>
        <div className="absolute bottom-4 right-4 text-6xl text-red-500 font-serif rotate-180">A</div>
      </div>

      {/* Decorative blurs */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-red-200/50 dark:bg-red-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-rose-200/50 dark:bg-red-500/10 rounded-full blur-2xl"></div>

      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg flex items-center justify-center shadow-lg shadow-red-500/30">
              <Heart className="w-5 h-5 text-white fill-current" />
            </div>
            <span className="font-bold text-gray-900 dark:text-white text-lg"><InlineTranslationEdit translationKey="CharityAceOfHeartsTitle" value={c('CharityAceOfHeartsTitle', 'Ace of Hearts')} fallback="Ace of Hearts" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
          </div>
          <div className="flex gap-1">
            {['hearts', 'diamonds', 'clubs', 'spades'].map((suit, i) => (
              <span key={suit} className={`text-lg ${i < 2 ? 'text-red-500' : 'text-gray-400 dark:text-gray-400'}`}>
                {suit === 'hearts' ? '♥' : suit === 'diamonds' ? '♦' : suit === 'clubs' ? '♣' : '♠'}
              </span>
            ))}
          </div>
        </div>

        {/* Playing Card with Charity Logo */}
        <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-white dark:to-gray-100 rounded-xl p-6 mb-4 shadow-md border border-gray-200 dark:border-transparent">
          <div className="absolute top-2 left-3 text-red-500 font-bold text-xl">A</div>
          <div className="absolute top-6 left-3 text-red-500 text-lg">♥</div>
          <div className="absolute bottom-2 right-3 text-red-500 font-bold text-xl rotate-180">A</div>
          <div className="absolute bottom-6 right-3 text-red-500 text-lg rotate-180">♥</div>

          <div className="flex flex-col items-center py-4">
            <div className="relative mb-3">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center shadow-lg border-4 border-white">
                <img
                  src={brandLogo}
                  alt={brandName}
                  className="w-14 h-14 object-contain rounded-full"
                />
              </div>
              <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-br from-red-500 to-rose-600 rounded-full flex items-center justify-center shadow-md">
                <Heart className="w-4 h-4 text-white fill-current" />
              </div>
            </div>
            <h4 className="font-bold text-gray-900 text-lg">{brandName}</h4>
            <p className="text-sm text-gray-600 text-center mt-1">{charityConfig.tagline}</p>
          </div>
        </div>

        {/* Impact Statement */}
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-4 mb-4">
          <p className="text-sm text-red-700 dark:text-red-100 text-center leading-relaxed">
            <Heart className="w-4 h-4 inline-block mr-1 text-red-500 dark:text-red-400" />
            {charityConfig.impactStatement}
          </p>
        </div>

        {/* Link */}
        <div className="text-center">
          <a
            href={charityConfig.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors group"
          >
            <InlineTranslationEdit translationKey="CharityAceOfHeartsCTA" value={c('CharityAceOfHeartsCTA', 'Supporting Our Community')} fallback="Supporting Our Community" onUpdate={handleTranslationUpdate} editMode={editMode} />
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
}
